import {
  deriveKeyFromCode, deriveKeyFromQR, fromBase64url, newKeyPair, newQRKey, newSession, openJSON, sealJSON, toBase64url,
  type AppConfig, type Envelope, type KeyPair, type MsgEnvelope, type PhoneMessage, type RelayMessage, type WebMessage,
} from '@tellvo/setup-protocol';
import { RelayClient } from './relay.js';

export type SessionPhase =
  | { kind: 'connecting' }
  /** `expiresAt`: when the relay drops the room if no phone came (epoch ms, this browser's clock). */
  | { kind: 'waiting'; code: string | null; link: string; expiresAt: number }
  /** The code ran out with no phone; a new one is a click away (`renew`). */
  | { kind: 'expired' }
  /** No phone yet and the relay went away: rejoining by itself (`offline` after a minute). */
  | { kind: 'reconnecting' }
  | { kind: 'offline' }
  | { kind: 'confirm' }
  | { kind: 'paired' }
  /** Paired, but the phone is in the background: the page keeps editing, the phone catches up. */
  | { kind: 'away' }
  | { kind: 'error'; reason: ErrorReason; code?: string };

/** Why the session stopped; the page words it from its catalog. `lost` is the relay going away. */
export type ErrorReason = 'lost' | 'unreachable' | 'rejected' | 'decrypt' | 'bye' | 'expired' | 'roomFull' | 'tooMany' | 'relay';

/** The page's own link to the relay, apart from where the phone is. */
export type Connection = 'ok' | 'reconnecting' | 'offline';

export interface SessionListener {
  phase(phase: SessionPhase): void;
  message(message: PhoneMessage): void;
  connection?(state: Connection): void;
}

/** How long the relay keeps a room no phone has joined (its ROOM_TTL_MS default). */
export const PAIRING_TTL_MS = 120_000;
/** Rejoining the room after the relay went away: these pauses, then the last one, until… */
const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000];
/** …this long without the relay, when the page says so and stops trying on its own. */
export const RECONNECT_GIVE_UP_MS = 60_000;

/**
 * The browser's side of docs/SETUP-PROTOCOL.md: a room, a QR key for the link, an X25519 pair
 * for the code path. Which key is used is decided by how the phone shows up: by session (QR)
 * or by code (hello exchange, then a confirmation). A code that ran out is replaced by a new
 * room in place (`renew`), without reloading the page.
 */
export class WebSession {
  private sessionId = newSession();
  private qrKeyBytes = newQRKey();
  private pair: KeyPair = newKeyPair();
  private key: Uint8Array | null = null;
  private pendingPhoneKey: Uint8Array | null = null;
  /** Sealed messages that arrived before the user allowed the phone (code mode). */
  private pendingSealed: MsgEnvelope[] = [];
  private relay: RelayClient;
  /** Set when this page ends the connection itself (expiry, reject, bye, a relay error): the
   *  socket closing after that is not a lost connection. */
  private stopped = false;
  /** The phone left after pairing; the session stays (protocol 3). */
  private phoneAway = false;
  /** The phone is back, its snapshot not yet: the page's changes go after it, on top. */
  private awaitingSnapshot = false;
  /** Set while the relay is gone; the time it went, for giving up. */
  private droppedAt: number | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;

  constructor(
    private readonly relayURL: string,
    private readonly pageOrigin: string,
    private readonly listener: SessionListener,
    /** Tests shorten the pauses; the page uses the real ones. */
    private readonly timing = { delays: RECONNECT_DELAYS_MS, giveUpMs: RECONNECT_GIVE_UP_MS },
  ) {
    this.relay = this.newRelay();
  }

  get session(): Uint8Array {
    return this.sessionId;
  }

  get qrKey(): Uint8Array {
    return this.qrKeyBytes;
  }

  get link(): string {
    return `${this.pageOrigin}/p/${toBase64url(this.sessionId)}#${toBase64url(this.qrKeyBytes)}`;
  }

  async start(): Promise<void> {
    this.listener.phase({ kind: 'connecting' });
    await this.join();
  }

  /** "Retry" after the page gave up: another minute of attempts, starting now. */
  retryNow(): void {
    if (this.droppedAt === null) return;
    this.droppedAt = Date.now();
    this.attempt = 0;
    this.report('reconnecting');
    if (!this.key) this.listener.phase({ kind: 'reconnecting' });
    void this.reconnect();
  }

  /** The code ran out on this page's clock: leave the room before the relay sweeps it. */
  expire(): void {
    if (this.key || this.stopped) return;
    this.stop();
    this.listener.phase({ kind: 'expired' });
  }

  /** A new room, code and keys, in place of an expired one. */
  async renew(): Promise<void> {
    if (this.key) return;
    this.stop();
    this.clearReconnect();
    this.sessionId = newSession();
    this.qrKeyBytes = newQRKey();
    this.pair = newKeyPair();
    this.pendingPhoneKey = null;
    this.pendingSealed = [];
    this.stopped = false;
    this.relay = this.newRelay();
    await this.join();
  }

  private async join(): Promise<void> {
    try {
      await this.relay.connect({ t: 'join', role: 'web', session: toBase64url(this.sessionId) });
    } catch (error) {
      void error;
      this.dropped();
    }
  }

  /**
   * The relay went away without the page asking. Nothing is lost: the keys and the queue of
   * changes are here, and the room can be entered again by its session — the QR stays the same,
   * a phone that is in it sends its state again. So the page rejoins by itself.
   */
  private dropped(): void {
    if (this.stopped) return;
    if (this.droppedAt === null) {
      this.droppedAt = Date.now();
      this.attempt = 0;
      // Changes wait in the queue meanwhile, as when the phone is in the background.
      if (this.key) this.phoneAway = true;
      this.report('reconnecting');
      if (!this.key) this.listener.phase({ kind: 'reconnecting' });
    }
    if (Date.now() - this.droppedAt >= this.timing.giveUpMs) {
      this.report('offline');
      if (!this.key) this.listener.phase({ kind: 'offline' });
      return;
    }
    const delays = this.timing.delays;
    const delay = delays[Math.min(this.attempt, delays.length - 1)] ?? 1_000;
    this.attempt += 1;
    this.clearTimer();
    this.retryTimer = setTimeout(() => void this.reconnect(), delay);
  }

  private async reconnect(): Promise<void> {
    this.clearTimer();
    this.relay.close();
    this.relay = this.newRelay();
    await this.join();
  }

  private clearTimer(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private clearReconnect(): void {
    this.clearTimer();
    this.droppedAt = null;
    this.attempt = 0;
  }

  private report(state: Connection): void {
    this.listener.connection?.(state);
  }

  /** Each connection reports only while it is the current one. */
  private newRelay(): RelayClient {
    const relay = new RelayClient(this.relayURL);
    relay.onEvent((event) => {
      if (relay !== this.relay) return;
      if (event.type === 'closed' && this.stopped) return;
      this.handle(event.type === 'relay' ? event.message : event.type === 'envelope' ? event.envelope : null);
    });
    return relay;
  }

  private stop(): void {
    this.stopped = true;
    this.clearTimer();
    this.relay.close();
  }

  /** Code mode: the user allowed the phone that said hello. */
  confirmPhone(): void {
    if (!this.pendingPhoneKey) return;
    this.key = deriveKeyFromCode(this.pair.secretKey, this.pendingPhoneKey, this.sessionId);
    this.pendingPhoneKey = null;
    this.listener.phase({ kind: 'paired' });
    // The phone sent its first snapshot right after hello, before we had the key.
    const queued = this.pendingSealed;
    this.pendingSealed = [];
    for (const sealed of queued) this.open(sealed);
  }

  rejectPhone(): void {
    this.pendingPhoneKey = null;
    this.stop();
    this.listener.phase({ kind: 'error', reason: 'rejected' });
  }

  /** Sends while the phone is there; false when it is not (the caller keeps what matters). */
  send(message: WebMessage): boolean {
    if (!this.key || this.phoneAway || this.awaitingSnapshot) return false;
    this.relay.send({ v: 1, t: 'msg', from: 'web', ...sealJSON(this.key, this.sessionId, 'web', message) });
    return true;
  }

  /** Asks the relay to wake the phone (a push, if it gave a token); harmless when it is here. */
  wake(): void {
    if (this.key && this.phoneAway) this.relay.send({ t: 'wake' });
  }

  bye(): void {
    this.send({ kind: 'bye' });
    this.stop();
  }

  private handle(message: RelayMessage | Envelope | null): void {
    if (!message) {
      this.dropped();
      return;
    }
    switch (message.t) {
      case 'joined':
        if (this.droppedAt !== null) {
          this.clearReconnect();
          this.report('ok');
          // Paired: the phone, if it is in the room, says so next (`peer`) and sends its state.
          if (this.key) {
            this.listener.phase({ kind: 'away' });
            return;
          }
        }
        this.listener.phase({ kind: 'waiting', code: message.code ?? null, link: this.link, expiresAt: Date.now() + PAIRING_TTL_MS });
        return;
      case 'peer':
        if (!message.present) {
          if (this.key) {
            this.phoneAway = true;
            this.awaitingSnapshot = false;
            this.listener.phase({ kind: 'away' });
          }
          return;
        }
        if (this.key) {
          // Back from the background: it sends its state first, the queue follows.
          this.phoneAway = false;
          this.awaitingSnapshot = true;
          return;
        }
        this.relay.send({ v: 1, t: 'hello', from: 'web', pub: toBase64url(this.pair.publicKey) });
        return;
      case 'error':
        // The relay closes the socket right after; that is not a lost connection.
        this.stopped = true;
        if (message.code === 'expired' && !this.key) {
          this.listener.phase({ kind: 'expired' });
          return;
        }
        this.listener.phase(relayError(message.code));
        return;
      case 'hello':
        if (message.from !== 'phone' || this.key) return;
        this.pendingPhoneKey = fromBase64url(message.pub);
        this.listener.phase({ kind: 'confirm' });
        return;
      case 'msg': {
        if (message.from !== 'phone') return;
        if (!this.key) {
          // The first sealed message decides the key: a phone that came by QR has the link key.
          const qrKey = deriveKeyFromQR(this.qrKeyBytes, this.sessionId);
          try {
            const inner = openJSON<PhoneMessage>(qrKey, this.sessionId, 'phone', message);
            this.key = qrKey;
            this.listener.phase({ kind: 'paired' });
            this.listener.message(inner);
          } catch {
            // Not the QR key: the phone came by code and the user has not allowed it yet.
            if (this.pendingPhoneKey) this.pendingSealed.push(message);
          }
          return;
        }
        this.open(message);
      }
    }
  }

  private open(message: MsgEnvelope): void {
    if (!this.key) return;
    let inner: PhoneMessage;
    try {
      inner = openJSON<PhoneMessage>(this.key, this.sessionId, 'phone', message);
    } catch {
      this.listener.phase({ kind: 'error', reason: 'decrypt' });
      return;
    }
    if (this.awaitingSnapshot && inner.kind === 'snapshot') {
      // State first, then the phase: the page replays its changes on `paired`.
      this.awaitingSnapshot = false;
      this.listener.message(inner);
      this.listener.phase({ kind: 'paired' });
      return;
    }
    this.listener.message(inner);
  }
}

function relayError(code: string): SessionPhase {
  switch (code) {
    case 'expired': return { kind: 'error', reason: 'expired' };
    case 'room_full': return { kind: 'error', reason: 'roomFull' };
    case 'too_many_attempts': return { kind: 'error', reason: 'tooMany' };
    default: return { kind: 'error', reason: 'relay', code };
  }
}

export type { AppConfig, PhoneMessage };
